import { AxiosResponse } from 'axios';
import _, { isEmpty, isNil, sortBy, times } from 'lodash';
import { DateTime } from 'luxon';
import IBusyApiWrapper from 'types/BusyApiWrapper';
import IMemberLocation from 'types/MemberLocation';
import IBreak from 'types/TimeEntryBreak';
import ITimeRange from 'types/TimeRange';
import TimeEntryBreakStatusType from 'types/enum/TimeEntryBreakStatusType';
import { Optional } from 'types/util/Optional';
import { dateUtils, timeEntryBreakUtils } from 'utils';
import { AxiosInstance } from 'utils/axiosClient';
import ITimeEntry, { IEntryRange } from '../types/TimeEntry';
import { dateTimeFromISOKeepZone, getSecondsFromIsoString, setZone } from './dateUtils';
import { getTotalAsHoursMinutesSeconds } from './stringUtils';
import { TimeEntryRange } from './timeEntryBreakUtils';
import { clampTimeRangeInsideDate } from './timeRangeUtils';

export function getTotalTimeFromEntryRangeAndBreakRanges(
  entryRange: { startTime: string; endTime?: Optional<string> },
  breakRanges: Array<{ startTime: string; endTime?: Optional<string> }>
) {
  const entryStartTime = dateUtils.dateTimeFromISOWithoutZone(entryRange.startTime);
  const entryEndTime = timeEntryEndTimeOrNow(entryRange);
  let totalEntryTime = entryEndTime.toMillis() - entryStartTime.toMillis();

  breakRanges.forEach((value) => {
    const breakStartTime = dateUtils.dateTimeFromISOWithoutZone(value.startTime);
    const breakEndTime = timeEntryBreakUtils.breakEndTimeOrNow(value);

    const status = timeEntryBreakUtils.getBreakStatus(value, entryRange);
    if (status === TimeEntryBreakStatusType.INSIDE_ENTRY) {
      totalEntryTime -= breakEndTime.toMillis() - breakStartTime.toMillis();
    } else if (status === TimeEntryBreakStatusType.END_TIME_OUTSIDE) {
      totalEntryTime -= entryEndTime.toMillis() - breakStartTime.toMillis();
    } else if (status === TimeEntryBreakStatusType.START_TIME_OUTSIDE) {
      totalEntryTime -= breakEndTime.toMillis() - entryStartTime.toMillis();
    } else if (status === TimeEntryBreakStatusType.CONTAINS_ENTRY) {
      totalEntryTime -= entryEndTime.toMillis() - entryStartTime.toMillis();
    }
  });

  if (totalEntryTime < 0) {
    totalEntryTime = 0;
  }
  return totalEntryTime / 1000;
}
/// Returns total in seconds.
export function getTotal(entry: ITimeEntry) {
  const filteredBreaks = entry.breaks.filter(({ deletedOn }) => deletedOn === null);
  return getTotalTimeFromEntryRangeAndBreakRanges(entry, filteredBreaks);
}

type BreakTotalPayload = {
  startTime: ITimeEntry['startTime'];
  endTime?: ITimeEntry['endTime'];
  breaks: Array<{
    startTime: ITimeEntry['breaks'][number]['startTime'];
    endTime?: ITimeEntry['breaks'][number]['endTime'];
    deletedOn: ITimeEntry['breaks'][number]['deletedOn'];
  }>;
};

export function getBreakTotal(entry: BreakTotalPayload) {
  const entryStartTime = dateUtils.dateTimeFromISOWithoutZone(entry.startTime);
  const entryEndTime = timeEntryEndTimeOrNow(entry);
  let totalBreakTime = 0;

  entry.breaks
    .filter((brk) => {
      return brk.deletedOn === null;
    })
    .forEach((val) => {
      const breakStartTime = dateUtils.dateTimeFromISOWithoutZone(val.startTime);
      const breakEndTime = timeEntryBreakUtils.breakEndTimeOrNow(val);

      const status = timeEntryBreakUtils.getBreakStatus(val, entry);
      if (status === TimeEntryBreakStatusType.INSIDE_ENTRY) {
        totalBreakTime += breakEndTime.toMillis() - breakStartTime.toMillis();
      } else if (status === TimeEntryBreakStatusType.END_TIME_OUTSIDE) {
        totalBreakTime += entryEndTime.toMillis() - breakStartTime.toMillis();
      } else if (status === TimeEntryBreakStatusType.START_TIME_OUTSIDE) {
        totalBreakTime += breakEndTime.toMillis() - entryStartTime.toMillis();
      } else if (status === TimeEntryBreakStatusType.CONTAINS_ENTRY) {
        totalBreakTime += entryEndTime.toMillis() - entryStartTime.toMillis();
      }
    });
  if (totalBreakTime < 0) {
    totalBreakTime = 0;
  }
  return totalBreakTime / 1000;
}

/**
 * Returns total in seconds for the time range of a time entry and the time ranges of the time entry's breaks.
 * If creating a time range from a time entry/break and using `DateTime.fromIso()` make sure to pass the option
 * `{setZone: true})` so that the total can be calculated appropriately if the data is in a different timezone.
 *
 * @param entryRange the time range that represents the time entry
 * @param breakRanges list of time ranges that represent the time entry's break entries
 */
export function getTotalFromDateTimes(entryRange: ITimeRange<DateTime>, breakRanges: Array<ITimeRange<DateTime>>) {
  const entryStart = dateUtils.dateTimeFromISOWithoutZone(entryRange.startTime.toISO());
  const entryEnd = dateUtils.dateTimeFromISOWithoutZone(entryRange.endTime.toISO());
  let totalEntryTime = Math.abs(entryEnd.toMillis() - entryStart.toMillis());

  for (const range of breakRanges) {
    const breakRangeStart = range.startTime.toUTC(0, { keepLocalTime: true });
    const breakRangeEnd = range.endTime.toUTC(0, { keepLocalTime: true });

    if (breakRangeStart >= entryStart && breakRangeEnd <= entryEnd) {
      // Break is entirely inside entry.
      totalEntryTime -= breakRangeEnd.toMillis() - breakRangeStart.toMillis();
    } else if (breakRangeStart <= entryStart && breakRangeEnd >= entryEnd) {
      // Break startTime and endTime is outside of entry.
      totalEntryTime = 0;
    } else if (breakRangeStart >= entryStart && breakRangeEnd >= entryEnd && breakRangeStart <= entryEnd) {
      // Breaks endTime is outside of entry.
      totalEntryTime -= entryEnd.toMillis() - breakRangeStart.toMillis();
    } else if (breakRangeStart <= entryStart && breakRangeEnd <= entryEnd && breakRangeEnd >= entryStart) {
      // Breaks startTime is outside of entry.
      totalEntryTime -= breakRangeEnd.toMillis() - entryStart.toMillis();
    }
  }

  if (totalEntryTime < 0) {
    totalEntryTime = 0;
  }
  return totalEntryTime / 1000;
}

/// Returns a date formatter like EEE, MMM d or EEE, MMM d, yyyy depending on the start / end time.
export function getEntryDateFormatter(timeEntry: ITimeEntry) {
  let dateFormatter = 'EEE, MMM d';

  const start = dateUtils.dateTimeFromISOWithoutZone(timeEntry.startTime);
  const end = timeEntryEndTimeOrNow(timeEntry);

  if (dateUtils.isSameYear(start, end) === false) {
    dateFormatter += ', yyyy';
  }
  return dateFormatter;
}

/// Returns true or false if the entry and or break has a location.
export function hasLocation(timeEntry: Pick<ITimeEntry, 'startLocation' | 'endLocation' | 'breaks'>) {
  return (
    !_.isNil(timeEntry.startLocation) ||
    !_.isNil(timeEntry.endLocation) ||
    (timeEntry.breaks &&
      !_.isEmpty(timeEntry.breaks) &&
      (timeEntry.breaks.find((b: IBreak) => !_.isNil(b.startLocation)) !== undefined ||
        timeEntry.breaks.find((b: IBreak) => !_.isNil(b.endLocation)) !== undefined))
  );
}

export async function checkIfEntryHasLocation(client: AxiosInstance, memberId: string, start: DateTime, end: DateTime) {
  return await client
    .get('/member-location?_version=3.2', {
      params: {
        'page_size': 1,
        'member_id': memberId,
        '_lte[time_gmt]': end.toSeconds(),
        '_gte[time_gmt]': start.toSeconds(),
      },
    })
    .then((res: AxiosResponse<IBusyApiWrapper<IMemberLocation[]>>) => {
      if (res.data && res.data.data) {
        return !_.isEmpty(res.data.data);
      } else {
        return false;
      }
    });
}

export function totalForDay(day: DateTime, entry: Pick<ITimeEntry, 'startTime' | 'endTime' | 'breaks'>) {
  let start = day.toUTC(0, { keepLocalTime: true }).startOf('day');
  let end = day.toUTC(0, { keepLocalTime: true }).endOf('day');
  const entryStart = dateUtils.dateTimeFromISOWithoutZone(entry.startTime);
  const entryEnd = timeEntryEndTimeOrNow(entry);

  // guard against the time entry being outside the entire day
  if ((entryStart < start && entryEnd < start) || (entryStart > end && entryEnd > end)) {
    return 0;
  }

  if (start < entryStart) {
    start = entryStart;
  }

  if (end > entryEnd) {
    end = entryEnd;
  }

  let totalEntryTime = Math.abs(end.toMillis() - start.toMillis());
  entry.breaks
    .filter((brk) => {
      const isNotDeleted = brk.deletedOn === null;
      const lessThanEnd = dateUtils.dateTimeFromISOWithoutZone(brk.startTime) < end;
      const greaterThanStart = brk.endTime
        ? dateUtils.dateTimeFromISOWithoutZone(brk.endTime!) >= start
        : DateTime.utc() >= start;
      return isNotDeleted && lessThanEnd && greaterThanStart;
    })
    .forEach((value) => {
      const breakStartTime = dateUtils.dateTimeFromISOWithoutZone(value.startTime);
      const breakEndTime = timeEntryBreakUtils.breakEndTimeOrNow(value);

      const status = timeEntryBreakUtils.getBreakStatus(value, {
        ...entry,
        startTime: start.toISO(),
        endTime: end.toISO(),
      });
      if (status === TimeEntryBreakStatusType.INSIDE_ENTRY) {
        totalEntryTime -= breakEndTime.toMillis() - breakStartTime.toMillis();
      } else if (status === TimeEntryBreakStatusType.END_TIME_OUTSIDE) {
        totalEntryTime -= end.toMillis() - breakStartTime.toMillis();
      } else if (status === TimeEntryBreakStatusType.START_TIME_OUTSIDE) {
        totalEntryTime -= breakEndTime.toMillis() - start.toMillis();
      } else if (status === TimeEntryBreakStatusType.CONTAINS_ENTRY) {
        totalEntryTime -= end.toMillis() - start.toMillis();
      }
    });
  if (totalEntryTime < 0) {
    totalEntryTime = 0;
  }
  return Number((totalEntryTime / 1000).toFixed(0));
}

export function breakTotalInRange(timeRange: ITimeRange<DateTime>, entry: ITimeEntry) {
  let start = timeRange.startTime.toUTC(0, { keepLocalTime: true });
  let end = timeRange.endTime.toUTC(0, { keepLocalTime: true });

  const entryStart = dateUtils.dateTimeFromISOWithoutZone(entry.startTime);
  const entryEnd = timeEntryEndTimeOrNow(entry);

  if (entryStart > start) {
    start = entryStart;
  }

  if (entryEnd < end) {
    end = entryEnd;
  }

  const entryInRange = { ...entry, startTime: start.toISO(), endTime: end.toISO() };
  const breakEntries = entry.breaks.filter((brk) => {
    const isNotDeleted = brk.deletedOn === null;
    const lessThanEnd = dateUtils.dateTimeFromISOWithoutZone(brk.startTime) < end;
    const greaterThanStart = brk.endTime
      ? dateUtils.dateTimeFromISOWithoutZone(brk.endTime!) >= start
      : DateTime.utc() >= start;
    return isNotDeleted && lessThanEnd && greaterThanStart;
  });

  return _.sumBy(breakEntries, (value) => {
    const breakStartTime = dateUtils.dateTimeFromISOWithoutZone(value.startTime);
    const breakEndTime = timeEntryBreakUtils.breakEndTimeOrNow(value);

    const status = timeEntryBreakUtils.getBreakStatus(value, entryInRange);
    if (status === TimeEntryBreakStatusType.INSIDE_ENTRY) {
      return (breakEndTime.toMillis() - breakStartTime.toMillis()) / 1000;
    } else if (status === TimeEntryBreakStatusType.END_TIME_OUTSIDE) {
      return (end.toMillis() - breakStartTime.toMillis()) / 1000;
    } else if (status === TimeEntryBreakStatusType.START_TIME_OUTSIDE) {
      return (breakEndTime.toMillis() - start.toMillis()) / 1000;
    } else if (status === TimeEntryBreakStatusType.CONTAINS_ENTRY) {
      return (end.toMillis() - start.toMillis()) / 1000;
    } else {
      return 0;
    }
  });
}

export function timeEntryEndTimeOrNow(entry: Pick<ITimeEntry, 'startTime' | 'endTime'>): DateTime {
  if (!isNil(entry.endTime)) {
    return dateUtils.dateTimeFromISOWithoutZone(entry.endTime);
  } else {
    return DateTime.local()
      .setZone(dateTimeFromISOKeepZone(entry.startTime).zoneName)
      .toUTC(0, { keepLocalTime: true })
      .startOf('minute');
  }
}

/**
 *  This function will convert the newEndTime to be localized to the timezone of the start time,
 *  and then it puts back in the end time timezone so the metaoffset can be properly set.
 *
 *
 *  @param existingEntry the entry being modified
 *  @param newEndTime the new end time being set by the user
 */
export function getEndTimeForSubmission(existingEntry: IEntryRange, newEndTime: DateTime, timezone?: string): DateTime {
  const entryStarTime = dateUtils.dateTimeFromISOKeepZone(existingEntry.startTime);
  const entryEndTime =
    existingEntry.endTime !== null ? dateUtils.dateTimeFromISOKeepZone(existingEntry.endTime!) : newEndTime;
  return !isNil(timezone)
    ? setZone(setZone(newEndTime, timezone, false), timezone, false)
    : setZone(setZone(newEndTime, entryStarTime.zoneName, false), entryEndTime.zoneName, true);
}

/**
 *
 * This function will properly localize the end time.
 *
 * It reverses the end time being localized by the start time and then it uses the metaoffset to put end time in its proper timezone.
 *
 *  @param entry start time / end time representing time entry
 *
 */
export function displayEndTime(entry: IEntryRange): DateTime | undefined {
  const startTime = dateUtils.dateTimeFromISOKeepZone(entry.startTime);
  let endTime;
  if (!isNil(entry.endTime)) {
    endTime = setZone(
      setZone(dateUtils.dateTimeFromISOWithoutZone(entry.endTime), startTime.zoneName, true),
      dateUtils.dateTimeFromISOKeepZone(entry.endTime).zoneName
    );
  }
  return endTime;
}

export function isTimeEntryOpen(timeEntry: Pick<ITimeEntry, 'endTime'>) {
  return !timeEntry.endTime;
}

export function isTimeEntryClosed(timeEntry: Pick<ITimeEntry, 'endTime'>) {
  return !isTimeEntryOpen(timeEntry);
}

/**
 * This function will get the end time as a seconds value
 *  @param entry the time entry to get requested end time
 */
export function getEndTimeSeconds(entry: ITimeEntry) {
  return entry.endTime ? DateTime.fromISO(entry.endTime!).toMillis() / 1000 : undefined;
}

export function getEquipmentAddOnDisplayName(entry: ITimeEntry, includeAddOns: boolean): string {
  let equipmentTitle = entry.equipment?.equipmentName ?? '';
  if (includeAddOns && !isNil(entry.addOns)) {
    const sortedAddOns = sortBy(
      entry.addOns.filter((addOn) => isNil(addOn.deletedOn)),
      (addOn) => addOn.equipment.equipmentName.toLowerCase()
    );

    if (!isEmpty(equipmentTitle) && !isEmpty(sortedAddOns)) {
      equipmentTitle += ', ';
    }

    equipmentTitle = equipmentTitle + sortedAddOns?.map((addOn) => addOn.equipment.equipmentName).join(', ');
  }

  return equipmentTitle;
}

export function getTimeEntryWorkedRanges(timeEntryRange: TimeEntryRange) {
  const orderedByStartTimeBreakRanges = sortBy(timeEntryRange.breaksInRange, (breakInRange) =>
    getSecondsFromIsoString(breakInRange.startTime)
  );

  const segmentCount = orderedByStartTimeBreakRanges.length + 1;

  const segments = times(segmentCount, (n) => {
    const previousBreak = orderedByStartTimeBreakRanges[n - 1];
    const startTime =
      previousBreak && previousBreak.endTime
        ? getSecondsFromIsoString(previousBreak.endTime)
        : getSecondsFromIsoString(timeEntryRange.startTime);

    const isLastSegment = n === orderedByStartTimeBreakRanges.length;

    const endTime = isLastSegment
      ? getSecondsFromIsoString(timeEntryRange.endTime)
      : getSecondsFromIsoString(orderedByStartTimeBreakRanges[n].startTime);
    return { startTime, endTime };
  });

  // NOTE: filters case when breaks are started back to back.
  return segments.filter((segment) => segment.endTime !== segment.startTime);
}

export function getTimeEntryWorkedRangesIso(timeEntryRange: TimeEntryRange) {
  const orderedByStartTimeBreakRanges = sortBy(timeEntryRange.breaksInRange, (breakInRange) =>
    getSecondsFromIsoString(breakInRange.startTime)
  );

  const segmentCount = orderedByStartTimeBreakRanges.length + 1;

  const segments = times(segmentCount, (n) => {
    const previousBreak = orderedByStartTimeBreakRanges[n - 1];
    const startTime = previousBreak && previousBreak.endTime ? previousBreak.endTime : timeEntryRange.startTime;

    const isLastSegment = n === orderedByStartTimeBreakRanges.length;

    const endTime = isLastSegment ? timeEntryRange.endTime : orderedByStartTimeBreakRanges[n].startTime;
    return { startTime, endTime };
  });

  // NOTE: filters case when breaks are started back to back.
  return segments.filter((segment) => segment.endTime !== segment.startTime);
}

export function clampTimeEntryRangeToDate(timeEntryRange: TimeEntryRange, date: DateTime): TimeEntryRange {
  const desiredRange = {
    startTime: DateTime.fromISO(timeEntryRange.startTime, { setZone: true }),
    endTime: DateTime.fromISO(timeEntryRange.endTime, { setZone: true }),
  };

  const clampedRange = clampTimeRangeInsideDate(desiredRange, date);

  return {
    id: timeEntryRange.id,
    startTime: clampedRange.startTime.toISO(),
    endTime: clampedRange.endTime.toISO(),
    breaksInRange: timeEntryRange.breaksInRange.map((breakInRange) => {
      const desiredBreakInRange = {
        startTime: DateTime.fromISO(breakInRange.startTime, { setZone: true }),
        endTime: breakInRange.endTime
          ? DateTime.fromISO(breakInRange.endTime, { setZone: true })
          : desiredRange.endTime,
      };

      const clampedBreakRange = clampTimeRangeInsideDate(desiredBreakInRange, date);

      return { startTime: clampedBreakRange.startTime.toISO(), endTime: clampedBreakRange.endTime.toISO() };
    }),
  };
}

/**
 * Generates a string representing the time range of a time entry for a specific date.
 * 
 * @param {ITimeEntry} timeEntry - The time entry object containing start and end times.
 * @param {DateTime} date - The specific date for which the time range is calculated.
 * @returns {string} A formatted string representing the time range (e.g., "8:00 AM – 6:00 PM", "12:00 AM* – 6:00 PM
").
 *                   Returns '---' if the time entry does not overlap with the given date.
 *                   Handles multi-day entries by including day-specific formatting.
 */
export function getTimeEntryRangeStringForDate(timeEntry: ITimeEntry, date: DateTime) {
  const entryStart = dateTimeFromISOKeepZone(timeEntry.startTime);
  const entryEnd = displayEndTime(timeEntry) ?? DateTime.local();

  const dayStart = date.startOf('day');
  const dayEnd = date.endOf('day');

  const start = entryStart > dayStart ? entryStart : dayStart;
  const end = entryEnd < dayEnd ? entryEnd : dayEnd;

  if (start > end) {
    return '---';
  }

  const showMultiDay = !entryStart.hasSame(entryEnd, 'day');

  const totalString = getTotalAsHoursMinutesSeconds(totalForDay(date, timeEntry));

  const isFirstDay = entryStart.hasSame(date, 'day');
  const isLastDay = entryEnd.hasSame(date, 'day');

  const startStr = getEntryStartTimeString(showMultiDay, isFirstDay, totalString, start);
  const endStr = getEntryEndTimeString(showMultiDay, isLastDay, totalString, end);

  return `${startStr} – ${endStr}`;
}

export function getEntryStartTimeString(
  showMultiDay: boolean,
  isFirstDay: boolean,
  totalString: string,
  date: DateTime
) {
  if (showMultiDay) {
    if (totalString === '23:59' || !isFirstDay) {
      return date.startOf('day').toFormat('t*');
    }
    return date.toFormat('t');
  }
  return date.toFormat('t');
}

export function getEntryEndTimeString(showMultiDay: boolean, isLastDay: boolean, totalString: string, date?: DateTime) {
  if (date) {
    if (showMultiDay) {
      if (totalString === '23:59' || !isLastDay) {
        return date.endOf('day').toFormat('t*');
      }
      return date.toFormat('t');
    }
    return date.toFormat('t');
  }
  return '---';
}
